/*
 * Attachment files (2026-10-28, ENHANCEMENTS S47): which files may be
 * attached, what they may be called, and whether their bytes are what their
 * name says.
 *
 * An attachment is a file a reader DOWNLOADS - a manual, a sample workbook, a
 * Power BI template - kept in a library (kb.Attachment) and stored as a blob in
 * the website's storage account. It is a separate thing from an inline image
 * (articleImages.ts): an image is shown inside the text and its bytes live in
 * the database; an attachment is listed under an article or linked from its
 * text with [[attachment:<id>]] (attachmentLinks.ts), and is only ever
 * downloaded, from the storage account, with a short-lived SAS.
 *
 * Shared because the browser runs the same checks before uploading - so an
 * author is told at once rather than after 25 MB has crossed the wire - and the
 * server runs them again, because the browser is not trusted, only helpful.
 *
 * THE ALLOW-LIST IS THE OWNER'S (2026-10-28): PDF, Word, Excel, PowerPoint,
 * text/CSV/JSON/XML, ZIP, Power BI and images. Read as file formats:
 *
 *   - the Office formats are the OOXML ones only. Macro-enabled files (.docm,
 *     .xlsm, .pptm) and the legacy binary formats (.doc, .xls, .ppt) are
 *     refused: both can carry macros, and a knowledge base handing out
 *     macro-bearing documents under its own name is what the allow-list exists
 *     to prevent;
 *   - Power BI is .pbix and .pbit, both ZIP containers. There is no registered
 *     media type for either, so both are stored as application/octet-stream;
 *   - images are the four raster formats articleImages.ts accepts, for its
 *     reasons - an SVG is a document that can carry script.
 *
 * Executables and scripts are refused by not being on the list, and again by
 * the byte checks: a renamed .exe is not a PDF, a ZIP, an image or text.
 *
 * kb.Attachment's CHECK lists the same media types; a new extension is a row in
 * ATTACHMENT_FILE_TYPES here, and a new media type is that row plus the CHECK.
 */
import { formatByteSize, sniffArticleImageContentType } from './articleImages.js';
/** 25 MB, the owner's limit (2026-10-28). kb.Attachment.ByteLength's CHECK is the same number. */
export const ATTACHMENT_MAX_BYTES = 25 * 1024 * 1024;
/** At most ten listed per article, the owner's number. kb.TR_ArticleAttachment_MaxPerArticle. */
export const ATTACHMENT_MAX_PER_ARTICLE = 10;
/** kb.Attachment.FileName is NVARCHAR(200). */
export const ATTACHMENT_FILE_NAME_MAX_LENGTH = 200;
export const ATTACHMENT_FILE_TYPES = [
    { extension: 'pdf', contentType: 'application/pdf', check: 'pdf', label: 'PDF' },
    {
        extension: 'docx',
        contentType: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        check: 'zip',
        label: 'Word document',
    },
    {
        extension: 'xlsx',
        contentType: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        check: 'zip',
        label: 'Excel workbook',
    },
    {
        extension: 'pptx',
        contentType: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
        check: 'zip',
        label: 'PowerPoint presentation',
    },
    { extension: 'txt', contentType: 'text/plain', check: 'text', label: 'text file' },
    { extension: 'csv', contentType: 'text/csv', check: 'text', label: 'CSV file' },
    { extension: 'json', contentType: 'application/json', check: 'text', label: 'JSON file' },
    { extension: 'xml', contentType: 'application/xml', check: 'text', label: 'XML file' },
    { extension: 'zip', contentType: 'application/zip', check: 'zip', label: 'ZIP archive' },
    { extension: 'pbix', contentType: 'application/octet-stream', check: 'zip', label: 'Power BI report' },
    { extension: 'pbit', contentType: 'application/octet-stream', check: 'zip', label: 'Power BI template' },
    { extension: 'png', contentType: 'image/png', check: 'image', label: 'PNG image' },
    { extension: 'jpg', contentType: 'image/jpeg', check: 'image', label: 'JPEG image' },
    { extension: 'jpeg', contentType: 'image/jpeg', check: 'image', label: 'JPEG image' },
    { extension: 'gif', contentType: 'image/gif', check: 'image', label: 'GIF image' },
    { extension: 'webp', contentType: 'image/webp', check: 'image', label: 'WEBP image' },
];
/** For an <input type="file" accept>: every allowed extension, dotted. */
export const ATTACHMENT_ACCEPT_ATTRIBUTE = ATTACHMENT_FILE_TYPES.map((type) => `.${type.extension}`).join(',');
/** The allowed extensions as an author reads them: ".pdf, .docx, ... and .webp". */
export function describeAttachmentFileTypes() {
    const dotted = ATTACHMENT_FILE_TYPES.map((type) => `.${type.extension}`);
    return `${dotted.slice(0, -1).join(', ')} and ${dotted[dotted.length - 1]}`;
}
export function describeAttachmentSizeLimit() {
    return `Attachments must be ${formatByteSize(ATTACHMENT_MAX_BYTES)} or smaller.`;
}
export function describeAttachmentCountLimit() {
    return `An article may list at most ${ATTACHMENT_MAX_PER_ARTICLE} attachments.`;
}
/** The extension after the last dot, lower case, or '' when there is none. */
export function attachmentExtension(fileName) {
    const dot = fileName.lastIndexOf('.');
    return dot < 0 ? '' : fileName.slice(dot + 1).toLowerCase();
}
/** The allowed type a name's extension stands for, or null when it is not on the list. */
export function attachmentFileTypeForName(fileName) {
    const extension = attachmentExtension(fileName);
    return ATTACHMENT_FILE_TYPES.find((type) => type.extension === extension) ?? null;
}
/** The characters kb.Attachment's CHECK refuses, and the control characters it leaves to us. */
const FORBIDDEN_FILE_NAME_CHARACTERS = /[\\/:*?"<>|\u0000-\u001f\u007f]/;
/**
 * The name as it will be stored: Unicode NFC (a name typed on a Mac arrives
 * decomposed, and two spellings of "å" must not be two names) with the
 * surrounding whitespace taken off. Applied before validation, by the browser
 * and the server alike, so both judge the same string.
 */
export function normaliseAttachmentFileName(fileName) {
    return fileName.normalize('NFC').trim();
}
/**
 * Why a (normalised) name cannot be stored, or null when it can. The message is
 * written for an author and drops into a field error as it is.
 */
export function attachmentFileNameProblem(fileName) {
    if (fileName.length === 0)
        return 'The file needs a name.';
    if (fileName.length > ATTACHMENT_FILE_NAME_MAX_LENGTH) {
        return `File names may be at most ${ATTACHMENT_FILE_NAME_MAX_LENGTH} characters.`;
    }
    if (FORBIDDEN_FILE_NAME_CHARACTERS.test(fileName)) {
        return 'File names may not contain \\ / : * ? " < > | or control characters.';
    }
    if (fileName.startsWith('.'))
        return 'File names may not start with a dot.';
    if (fileName !== fileName.trim())
        return 'File names may not start or end with a space.';
    if (!attachmentFileTypeForName(fileName)) {
        return `Attach one of these kinds of file: ${describeAttachmentFileTypes()}. Others are refused - executables and scripts in particular.`;
    }
    return null;
}
/**
 * The whole gate on a file, from its (normalised) name and its bytes: the name
 * rules, the size, and the check that the bytes are the kind of file the name
 * says. Advisory in the browser (which may pass only the first bytes - the
 * checks read at most 8 KB - and the size it knows), authoritative on the
 * server.
 */
export function inspectAttachment(fileName, bytes, byteLength = bytes.byteLength) {
    const nameProblem = attachmentFileNameProblem(fileName);
    if (nameProblem)
        return { ok: false, message: nameProblem };
    if (byteLength === 0)
        return { ok: false, message: 'That file is empty.' };
    if (byteLength > ATTACHMENT_MAX_BYTES) {
        return {
            ok: false,
            message: `That file is ${formatByteSize(byteLength)}. ${describeAttachmentSizeLimit()}`,
        };
    }
    const fileType = attachmentFileTypeForName(fileName);
    const view = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes);
    if (!bytesMatch(fileType, view)) {
        return {
            ok: false,
            message: `${fileName} is not a ${fileType.label}: its contents do not match its .${fileType.extension} ` +
                'extension. Attach the file as it was saved, or rename it to what it really is.',
        };
    }
    return { ok: true, fileType };
}
/** How many leading bytes the checks read - what a browser needs to pass. */
export const ATTACHMENT_SNIFF_BYTES = 8192;
function bytesMatch(fileType, view) {
    switch (fileType.check) {
        case 'pdf':
            return indexOf(view.subarray(0, 1024), PDF_HEADER) >= 0;
        case 'zip':
            return startsWith(view, ZIP_LOCAL_FILE) || startsWith(view, ZIP_EMPTY_ARCHIVE);
        case 'image':
            return sniffArticleImageContentType(view) === fileType.contentType;
        case 'text': {
            if (startsWith(view, [0xff, 0xfe]) || startsWith(view, [0xfe, 0xff]))
                return true;
            return view.subarray(0, ATTACHMENT_SNIFF_BYTES).indexOf(0) < 0;
        }
    }
}
const PDF_HEADER = [0x25, 0x50, 0x44, 0x46, 0x2d]; // %PDF-
const ZIP_LOCAL_FILE = [0x50, 0x4b, 0x03, 0x04]; // PK\3\4
const ZIP_EMPTY_ARCHIVE = [0x50, 0x4b, 0x05, 0x06]; // PK\5\6
function startsWith(view, signature) {
    return view.length >= signature.length && signature.every((byte, index) => view[index] === byte);
}
function indexOf(view, signature) {
    for (let start = 0; start + signature.length <= view.length; start += 1) {
        if (signature.every((byte, offset) => view[start + offset] === byte))
            return start;
    }
    return -1;
}
/**
 * The upload's query string, the one spelling both clients use: the body is the
 * file, so the name travels here (articleImageUploadQuery's convention).
 */
export function attachmentUploadQuery(fileName) {
    return new URLSearchParams({ fileName }).toString();
}
//# sourceMappingURL=attachmentFiles.js.map